/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 *
 * The drawer is organised into three labelled groups so the primary jobs are
 * obvious at a glance:
 *   1. Browse  - the search entry point plus the hosted custom links.
 *   2. Sell    - the "Sell your emoto" primary CTA.
 *   3. Account - inbox, listings, settings, log out (authenticated only).
 *
 * All rows are >=44px tall and carry a visible :focus-visible ring.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    const handleLinkClick = () => {
      if (to?.hash && typeof window !== 'undefined') {
        const idString = to.hash.replace('#', '');
        const target = document.getElementById(idString);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to} className={css.rowLink} onClick={handleLinkClick}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href} className={css.rowLink}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @param {Function?} props.onSearchOpen opens the mobile search modal
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    onSearchOpen,
    showSearchForm = true,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const intl = useIntl();
  const menuNavLabel = intl.formatMessage({ id: 'TopbarMobileMenu.screenreader.menuNavigation' });

  // The hosted top-bar asset may be missing entirely; default to an empty list.
  const safeCustomLinks = Array.isArray(customLinks) ? customLinks : [];
  const extraLinks = safeCustomLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // Obvious search entry point at the top of the drawer. It reuses the mobile
  // search modal that the Topbar already owns, so there is a single code path.
  const searchEntryMaybe =
    showSearchForm && onSearchOpen ? (
      <InlineTextButton
        rootClassName={css.searchEntry}
        onClick={onSearchOpen}
        type="button"
        id="mobile-menu-search"
      >
        <svg
          className={css.searchEntryIcon}
          width="18"
          height="18"
          viewBox="0 0 18 18"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <g
            transform="matrix(-1 0 0 1 17 1)"
            strokeWidth="2"
            fill="none"
            fillRule="evenodd"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11.733 11.733l3.727 3.727" />
            <circle cx="6.4" cy="6.4" r="6.4" />
          </g>
        </svg>
        <span className={css.searchEntryLabel}>
          <FormattedMessage id="TopbarMobileMenu.searchEntry" />
        </span>
      </InlineTextButton>
    ) : null;

  // Primary CTA. Routed via NamedLink + route config, never a hardcoded path.
  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const sellGroupMaybe = showCreateListingsLink ? (
    <section className={css.group} aria-labelledby="mobile-menu-sell-heading">
      <h2 className={css.groupHeading} id="mobile-menu-sell-heading">
        <FormattedMessage id="TopbarMobileMenu.sellHeading" />
      </h2>
      <p className={css.groupHelp}>
        <FormattedMessage id="TopbarMobileMenu.sellHelp" />
      </p>
      {createListingsLinkMaybe}
    </section>
  ) : null;

  const browseGroupMaybe =
    searchEntryMaybe || extraLinks.length > 0 ? (
      <section className={css.group} aria-labelledby="mobile-menu-browse-heading">
        <h2 className={css.groupHeading} id="mobile-menu-browse-heading">
          <FormattedMessage id="TopbarMobileMenu.browseHeading" />
        </h2>
        {searchEntryMaybe}
        {extraLinks.length > 0 ? <ul className={css.customLinksWrapper}>{extraLinks}</ul> : null}
      </section>
    ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );
    return (
      <nav className={css.root} aria-label={menuNavLabel}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          {browseGroupMaybe}
          {sellGroupMaybe}

          <div className={css.spacer} />
        </div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage" className={css.rowLink}>
        <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
      </NamedLink>
    </li>
  ) : null;

  return (
    <nav className={css.root} aria-label={menuNavLabel}>
      <div className={css.content}>
        <div className={css.identity}>
          <AvatarLarge className={css.avatar} user={currentUser} />
          <span className={css.greeting}>
            <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
          </span>
        </div>

        {browseGroupMaybe}
        {sellGroupMaybe}

        <section className={css.group} aria-labelledby="mobile-menu-account-heading">
          <h2 className={css.groupHeading} id="mobile-menu-account-heading">
            <FormattedMessage id="TopbarMobileMenu.accountHeading" />
          </h2>
          <ul className={css.accountLinksWrapper}>
            <li className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}>
              <NamedLink name="InboxPage" params={{ tab: inboxTab }} className={css.rowLink}>
                <FormattedMessage id="TopbarMobileMenu.inboxLink" />
                {notificationCountBadge}
              </NamedLink>
            </li>
            {manageListingsLinkMaybe}
            <li className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}>
              <NamedLink name="ProfileSettingsPage" className={css.rowLink}>
                <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
              </NamedLink>
            </li>
            <li className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}>
              <NamedLink name="AccountSettingsPage" className={css.rowLink}>
                <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
              </NamedLink>
            </li>
          </ul>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </section>

        <div className={css.spacer} />
      </div>
    </nav>
  );
};

export default TopbarMobileMenu;
